import { useCallback, useContext, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  getMasterPortfolioV3,
  getVennDemoPortfolio,
  setAsMasterPortfolioV3,
  updatePortfolioV3,
  type Portfolio,
} from 'venn-api';
import { PortfoliosContext, type PortfoliosContextValue } from 'venn-components';
import { proxySetIdState } from 'venn-state';
import { useForbiddenNotification } from './useForbiddenNotification';

const NO_MASTER_ACCESS_MESSAGE =
  'You do not have access to the master portfolio. Contact your administrator for assistance.';

export const usePortfoliosContext = () => {
  const notifyMasterForbidden = useForbiddenNotification(NO_MASTER_ACCESS_MESSAGE);

  const proxySetId = useRecoilValue(proxySetIdState);
  const setDemoPortfolio = useCallback(async () => {
    try {
      const { content } = await getVennDemoPortfolio(proxySetId);
      setPortfoliosContext((c) => ({
        ...c,
        demoPortfolio: content,
      }));
    } catch {
      setPortfoliosContext((c) => ({
        ...c,
        demoPortfolio: undefined,
      }));
    }
  }, [proxySetId]);

  const setMasterPortfolio = useCallback(async () => {
    try {
      const { content } = await getMasterPortfolioV3(proxySetId);
      setPortfoliosContext((c) => ({
        ...c,
        masterPortfolio: content,
      }));
    } catch (e: unknown) {
      setPortfoliosContext((c) => ({
        ...c,
        masterPortfolio: undefined,
      }));
      notifyMasterForbidden(e);
    }
  }, [proxySetId, notifyMasterForbidden]);
  const refreshPortfolios = useCallback(async () => {
    try {
      setPortfoliosContext((c) => ({
        ...c,
        loading: true,
      }));
      await Promise.all([setMasterPortfolio(), setDemoPortfolio()]);
    } catch {
      // Do nothing
    }
    setPortfoliosContext((c) => ({
      ...c,
      loading: false,
    }));
  }, [setDemoPortfolio, setMasterPortfolio]);
  const setMasterPortfolioById = useCallback(
    async (masterPortfolioId: number) => {
      setPortfoliosContext((c) => ({
        ...c,
        loading: true,
      }));
      await setAsMasterPortfolioV3(masterPortfolioId, proxySetId);
      await refreshPortfolios();
    },
    [proxySetId, refreshPortfolios],
  );

  const updateMaster = useCallback(
    async (master: Portfolio): Promise<Portfolio> => {
      setPortfoliosContext((c) => ({
        ...c,
        loading: true,
      }));
      const { content: masterPortfolio } = await updatePortfolioV3(master.id, proxySetId, master);
      setPortfoliosContext((c) => ({
        ...c,
        masterPortfolio,
        loading: false,
      }));

      return masterPortfolio;
    },
    [proxySetId],
  );
  const [portfoliosContext, setPortfoliosContext] = useState<PortfoliosContextValue>({
    ...useContext(PortfoliosContext),
    setAsMaster: setMasterPortfolioById,
    updateMaster,
    refresh: refreshPortfolios,
    loading: true,
  });
  useEffect(() => {
    refreshPortfolios();
  }, [refreshPortfolios]);
  return portfoliosContext;
};
